/**
 * English locale for extensions-ui.
 * Export is the "extensions" subtree: host apps merge this into their i18n
 * resources as translation.extensions so that t('extensions.list.heading') etc. resolve.
 */
import type { WidenLocale } from '@mangovoice/types'

export const extensionsEn = {
  // ---------------------------------------------------------------------------
  // Common / shared labels
  // ---------------------------------------------------------------------------
  common: {
    cancel: `Cancel`,
    delete: `Delete`,
    save: `Save`,
    apply: `Apply`,
    edit: `Edit`,
    actions: `Actions`,
    add: `Add`,
    all: `All`,
    loading: `Loading...`,
    noChange: `No change`,
    none: `None`,
  },

  // ---------------------------------------------------------------------------
  // List page
  // ---------------------------------------------------------------------------
  list: {
    pageLabel: `All Extensions`,
    heading: `Extensions`,
    linesHeading: `Lines`,
    searchPlaceholder: `Search by name or number`,
    linesSearchPlaceholder: `Search by name, number, assigned profile, or location`,
    loadingExtensions: `Loading extensions...`,
    noMatch: `No extensions match your search`,
    noExtensions: `No extensions found`,
    noMatchHint: `Try a different search term.`,
    noExtensionsHint: `There are no extensions for this PBX.`,
    activateAll: `Activate All`,
    deactivateAll: `Deactivate All`,
    massEdit: `Mass edit`,
    linesSelected: `{{count}} line{{suffix}} selected`,
  },

  // ---------------------------------------------------------------------------
  // Column headers
  // ---------------------------------------------------------------------------
  columns: {
    number: `Number`,
    name: `Name`,
    type: `Type`,
    created: `Created`,
    updated: `Updated`,
    lastUpdated: `Last updated`,
    assignedUser: `Assigned user`,
    assignedProfile: `Assigned profile`,
    location: `Location`,
    actions: `Actions`,
  },

  // ---------------------------------------------------------------------------
  // Row actions & badges
  // ---------------------------------------------------------------------------
  row: {
    inactive: `Inactive`,
    edit: `Edit`,
    delete: `Delete`,
    viewCallMetrics: `View call metrics`,
    activate: `Activate`,
    deactivate: `Deactivate`,
    extensionFallback: `Extension {{id}}`,
    selectAllLines: `Select all lines`,
    selectLine: `Select {{name}}`,
  },

  // ---------------------------------------------------------------------------
  // Delete dialog
  // ---------------------------------------------------------------------------
  deleteDialog: {
    title: `Delete Extension`,
    confirmation: `Are you sure you want to delete "{{displayName}}"? This action cannot be undone.`,
    unnamed: `Unnamed Extension`,
    reassignLabel: `Reassign references to`,
    deleteWithoutReassigning: `Delete without reassigning`,
  },

  // ---------------------------------------------------------------------------
  // Set active state dialog (single)
  // ---------------------------------------------------------------------------
  activeStateDialog: {
    activateTitle: `Activate extension`,
    deactivateTitle: `Deactivate extension`,
    body: `You are about to {{action}} {{displayName}}, which will cause all devices using this extension as the Primary Line Extension to be rebooted.`,
    unnamedLine: `Unnamed Line`,
    confirmPrompt: `Are you sure you want to continue?`,
    setActive: `Set active`,
    setInactive: `Set inactive`,
  },

  // ---------------------------------------------------------------------------
  // Bulk active state dialog
  // ---------------------------------------------------------------------------
  bulkActiveDialog: {
    activateTitle: `Activate all extensions`,
    deactivateTitle: `Deactivate all extensions`,
    body: `You are about to {{action}} all extensions, which will cause all devices using these extensions as the Primary Line Extension to be rebooted.`,
    confirmPrompt: `Are you sure you want to continue?`,
    activateAll: `Activate All`,
    deactivateAll: `Deactivate All`,
  },

  // ---------------------------------------------------------------------------
  // Mass edit dialog
  // ---------------------------------------------------------------------------
  massEdit: {
    title: `Mass edit lines`,
    description: `Update the following fields for {{count}} line{{suffix}}. Leave as "No change" to keep current value.`,
    outboundCallerId: `Outbound caller ID`,
    localPresence: `Local presence`,
    preferredCodec: `Preferred codec`,
    location: `Location`,
    locationPlaceholder: `No change or type to search`,
    locationSearchLabel: `Location search`,
    loadingLocations: `Loading locations\u2026`,
    noLocationsFound: `No locations found`,
    typeToSearchLocations: `Type to search locations`,
  },

  // ---------------------------------------------------------------------------
  // Pagination / toolbar
  // ---------------------------------------------------------------------------
  pagination: {
    resultsPerPage: `Results per page:`,
    previousPage: `Previous page`,
    nextPage: `Next page`,
    all: `All`,
    jumpToPage: `Jump to page {{page}}`,
    searchExtensions: `Search extensions`,
    toggleFilters: `Toggle filters`,
    showingAll: `Showing all {{total}} results`,
    showingRange: `Showing {{start}} to {{end}} of {{total}} results`,
  },

  // ---------------------------------------------------------------------------
  // Generic edit pages
  // ---------------------------------------------------------------------------
  edit: {
    validationError: `Validation Error`,
    reviewRequiredFields: `Please review required fields and try again.`,
    emailFormatInvalid: `Incorrect email formatting. Make sure there are no spaces. Separate multiple addresses with commas.`,
    nameNumberRequired: `Name and number are required.`,
    reservedNumber: `{{number}} is reserved. Please select a different number.`,
    createFailed: `Create failed`,
    updateFailed: `Update failed`,
    breadcrumbAdd: `Add {{typeLabel}}`,
    breadcrumbEdit: `Edit {{typeLabel}} {{value}}`,
    backToList: `← Back to {{listLabel}}`,
    wrongPbx: `This extension is not in the selected PBX`,
    wrongPbxDescription: `This extension does not belong to the selected PBX.`,
    openInPbx: `Open in {{pbxName}}`,
    loadFailed: `This extension could not be loaded`,
    options: `Options`,
    saveChanges: `Save changes`,
    omitFromDirectory: `Omit from directory`,
    nameRequired: `Name is required`,
    numberRequired: `Number is required`,
    extensionNumberPlaceholder: `Extension number`,
    yes: `Yes`,
    no: `No`,
    members: `Members`,
    membersNoun: `members`,
    userGroups: `User Groups`,
    userGroupsNoun: `user groups`,
    holdMusic: `Hold Music`,
    line: {
      requiredFields: `Name, number, location, and assigned profile are required.`,
      selectLocation: `Select A Location`,
      notType: `Not a line`,
      pbxUuid: `PBX UUID`,
      pbxName: `PBX`,
      sipSecret: `SIP Secret`,
      sipSecretReset: `SIP secret reset`,
      reset: `Reset`,
      extensionUuid: `Extension UUID`,
      dialNow: `Dial Now`,
      dialNowCleared: `Dial-now cleared`,
      clear: `Clear`,
      assignedProfileMobileDevices: `Assigned Profile's Mobile Devices`,
      addTitle: `Add Line`,
      title: `Line`,
      voicemailOptions: `Voicemail`,
      outDialExtension: `OutDial Extension`,
      outDialHelp: `OutDial extension help`,
      outDialHelpNo: `No: This extension acts as a regular extension.`,
      outDialHelpYes: `Yes: This extension only forwards to the Forwarding Number.`,
      outDialHelpBeAware: `Be aware:`,
      outDialHelpWarningOne: `The system cannot retrieve a call once it has forwarded to an outside number.`,
      outDialHelpWarningTwo: `You cannot use system voicemail with an OutDial extension.`,
      outDialEnabledHint: `OutDial is enabled. Calls forward to the Forwarding Number; Outbound Caller ID still applies on the outbound leg.`,
      outDialDisabledHint: `OutDial is disabled. Outbound caller settings apply.`,
      loadingProfiles: `Loading profiles…`,
      voicemailEnabled: `Voicemail enabled`,
      forwardingNumber: `Forwarding number (OutDial)`,
      localAreaCode: `Local Area Code`,
      localAreaCodeInvalid: `Area code is invalid. {{areaCode}} is not permitted for the Local Area Code.`,
      localAreaCodeNotAllowed: `The Area Code {{areaCode}} is not permitted for the Local Area Code.`,
      outboundCallerIdRequired: `Please select an Outbound Caller ID.`,
      outboundCallerIdNoOptions: `This PBX has no phone numbers configured. Add a phone number to the PBX before editing this line.`,
      outboundCallerIdMissingSavedValue: `This line is missing a valid Outbound Caller ID. Please select one and save before continuing.`,
      expandDetails: `Show details`,
      collapseDetails: `Hide details`,
      activate: `Activate`,
      deactivate: `Deactivate`,
      findMe: {
        title: `Find Me`,
        mode: `Find Me`,
        number: `Find Me Number`,
        delay: `Find Me After`,
        modeOptions: {
          '0': `Off`,
          '1': `On`,
          '2': `Ring All`,
        },
        delayOptions: {
          '0': `Immediately`,
          '5': `5 Seconds`,
          '10': `10 Seconds`,
          '15': `15 Seconds`,
        },
        numberRequired: `A Find Me Number must be given if Find Me is On.`,
        numberInvalid: `Find Me Number must contain only digits and an optional leading + (max 15 digits).`,
      },
      callRecording: `Call Recording`,
      callRecordingOptions: {
        '0': `No`,
        '1': `Yes`,
        '2': `System Defined`,
      },
      dedicatedFax: `Dedicated Fax`,
      inCallFeatureCodes: `In-Call Feature Codes Enabled`,
      createButton: `Create Line`,
      saveAndCreateAnother: `Save & Create Another`,
      details: `Line Details`,
      codecOpus: `Best for slow internet (OPUS) Supports: Web Phones, Yealink`,
      codecPcmu: `Standard (PCMU) Supports: All phone types`,
      codecG722: `HD Audio (G722) Supports: Yealink, Cisco`,
      codecG729: `DEPRECATED - Good for slow internet (G729) Supports: Yealink, Cisco`,
    },
    ringGroup: {
      ringing: `Ringing`,
      created: `Ring group created`,
      updated: `Ring group updated`,
      saveFailed: `Failed to save ring group`,
      notType: `Not a ring group`,
      notTypeDescription: `This extension is not a ring group. Use the Extensions list to edit other types.`,
      namePlaceholder: `Ring group name`,
      ringStrategy: `Ring Strategy`,
      ringAll: `Ring All`,
      pagingGroup: `Paging Group`,
      ringbackAudio: `Ringback Audio`,
      createButton: `Create Ring Group`,
      updateButton: `Update Ring Group`,
    },
    callQueue: {
      created: `Call queue created`,
      updated: `Call queue updated`,
      saveFailed: `Failed to save call queue`,
      notType: `Not a call queue`,
      queueSettings: `Queue Settings`,
      ringStrategy: `Ring Strategy`,
      strategyRingAll: `Ring All`,
      strategyRoundRobin: `Round Robin`,
      strategyLongestIdle: `Longest Idle`,
      strategyRandom: `Random`,
      strategyLeastTalkTime: `Least Talk Time`,
      strategyFewestCalls: `Fewest Calls`,
      preCallAnnouncement: `Pre Call Announcement`,
      maxNoAnswer: `Max no answer (attempts)`,
      busyDelayTime: `Busy delay time (seconds)`,
      rejectDelayTime: `Reject delay time (seconds)`,
      wrapUpTime: `Wrap-up time (seconds)`,
      noAnswerDelayTime: `No answer delay time (seconds)`,
      maxRingDuration: `Max ring duration (seconds)`,
      maxCallersInQueue: `Max callers in queue`,
      maxWaitTime: `Max wait time (seconds)`,
      timeoutDestination: `Timeout destination`,
      escapeDestination: `Escape destination`,
      destinationSearchPlaceholder: `Search extensions...`,
      destinationNoMatches: `No extension matches that search.`,
      destinationCurrentUnlisted: `{{value}} (current)`,
      periodicAnnouncements: `Periodic announcements`,
      positionAnnouncementFrequency: `Position announcement frequency`,
      positionAnnouncementMessage: `Position announcement message`,
      queuePosition: `Queue Position`,
      agentsNoun: `agents`,
      newButton: `New Call Queue`,
      updateButton: `Update Call Queue`,
    },
    conferenceRoom: {
      moderatorPinValidation: `Moderator PIN must be exactly 4 digits.`,
      conferencePinValidation: `Conference PIN must be exactly 4 digits.`,
      holdMusicRequired: `Hold Music is required.`,
      created: `Conference room created`,
      updated: `Conference room updated`,
      saveFailed: `Failed to save conference room`,
      notType: `Not a conference room`,
      moderatorRequired: `Moderator required`,
      moderatorPin: `Moderator PIN`,
      userPinRequired: `User PIN required`,
      conferencePin: `Conference PIN`,
      defaultMuted: `Default muted`,
      announceNewParticipants: `Announce new participants`,
      conferenceGreeting: `Conference greeting`,
      selectHoldMusic: `Select Hold Music`,
      createButton: `Create Conference Room`,
      updateButton: `Update Conference Room`,
    },
    virtualFax: {
      invalidEmail: `Please enter a valid email address.`,
      passwordsDoNotMatch: `Passwords do not match.`,
      passwordMinLength: `Secure fax password must be at least 8 characters.`,
      created: `Virtual fax created`,
      updated: `Virtual fax updated`,
      saveFailed: `Failed to save virtual fax`,
      secureFaxLinkResent: `Secure fax link resent`,
      resendFailed: `Resend failed`,
      resendSaveFailed: `Failed to resend secure fax link`,
      notType: `Not a virtual fax`,
      receivingOptions: `Receiving Options`,
      receivingEmailDestination: `Receiving email destination`,
      secureFax: `Secure Fax`,
      download: `Download`,
      singleDownload: `Single Download`,
      download5: `5 downloads`,
      download10: `10 downloads`,
      download15: `15 downloads`,
      failedLoginRetries: `Failed Login Retries`,
      retries5: `5 retries`,
      retries10: `10 retries`,
      retries15: `15 retries`,
      linkExpiresIn: `Download Link Expires In`,
      day1: `1 day`,
      day2: `2 days`,
      day3: `3 days`,
      day7: `7 days`,
      day15: `15 days`,
      day30: `30 days`,
      changePasswordOptional: `Change secure fax password (optional)`,
      userGroupsDescription: `Choose which user groups should have access to this Virtual Fax.`,
      sending: `Sending…`,
      resendSecureFaxLink: `Resend secure fax link`,
      createButton: `Create Virtual Fax Machine`,
      updateButton: `Update Virtual Fax Machine`,
    },
    shared: {
      generalOptions: `General Options`,
      voicemailTimeZone: `Voicemail time zone`,
      voicemailPassword: `Voicemail password`,
      playVoicemailInstructions: `Play voicemail instructions`,
      notificationOptions: `Notification Options`,
      sendEmailNotifications: `Send email notifications`,
      messageEmailAddress: `Message email address`,
      emailPlaceholder: `email@example.com`,
      attachVoicemailMessage: `Attach voicemail message`,
      deleteMessageAfterSend: `Delete message after send`,
      outgoingMessage: `Outgoing Message`,
      useSystemGreetings: `Use system greetings`,
      customGreeting: `Custom Greeting`,
    },
    sharedVoicemail: {
      created: `Shared voicemail created`,
      updated: `Shared voicemail updated`,
      saveFailed: `Failed to save shared voicemail`,
      notType: `Not a shared voicemail`,
      userGroupsDescription: `Choose which user groups should have access to this Shared Voicemail Box.`,
      createButton: `Create Shared Voicemail`,
      updateButton: `Update Shared Voicemail`,
    },
  },

  updateLocation: {
    title: `Update location`,
    description: `Update location for {{count}} line{{suffix}}?`,
    confirm: `Update location`,
  },

  pickList: {
    assignedWithLimit: `Assigned {{itemNoun}}: {{count}} / {{max}}`,
    assigned: `Assigned {{itemNoun}}: {{count}}`,
    noneAssigned: `No {{itemNoun}} added yet.`,
    remove: `Remove`,
    available: `Available {{itemNoun}}: {{count}}`,
    searchAvailable: `Search available {{itemNoun}}`,
    maxReached: `Maximum of {{max}} {{itemNoun}} reached.`,
    noneAvailable: `No {{itemNoun}} available.`,
  },

  localPresence: {
    0: `Off`,
    1: `Group 1`,
    2: `Group 2`,
    3: `Group 3`,
    4: `Group 4`,
    5: `Group 5`,
    6: `Group 6`,
  },

  codecOptions: {
    opus: `Best for slow internet (OPUS) — Supports: Web Phones, Yealink`,
    pcmu: `Standard (PCMU) — Supports: All phone types`,
    g722: `HD Audio (G722) — Supports: Yealink, Cisco`,
    g729: `DEPRECATED — Good for slow internet (G729) — Supports: Yealink, Cisco`,
    default: `System default`,
  },

  timezones: {
    america_new_york: `Eastern Time`,
    america_chicago: `Central Time`,
    america_denver: `Mountain Time`,
    america_los_angeles: `Pacific Time`,
    america_phoenix: `Arizona`,
    america_anchorage: `Alaska Time`,
    pacific_honolulu: `Hawaii Time`,
    america_toronto: `Toronto (Eastern)`,
    america_vancouver: `Pacific Time - British Columbia (PCT)`,
    europe_london: `London (GMT/BST)`,
    europe_paris: `Paris (CET)`,
    australia_sydney: `Sydney (AEST)`,
    utc: `UTC`,
  },

  // ---------------------------------------------------------------------------
  // Toast messages
  // ---------------------------------------------------------------------------
  toast: {
    deleteSuccess: `Extension deleted successfully.`,
    deleteFailed: `Delete failed`,
    deleteFailedDescription: `Failed to delete extension. Please try again.`,
    success: `Success`,
    failed: `Failed`,
    activeStateSuccess: `Line set to {{state}}.`,
    activeStateFailed: `Failed to update line active state.`,
    bulkSuccess: `{{count}} line{{suffix}} set to {{state}}.`,
    partialFailure: `Partial failure`,
    partialFailureDescription: `{{succeeded}} succeeded, {{failed}} failed. Some lines may not have been updated.`,
    massEditSuccess: `Updated {{count}} line{{suffix}}.`,
    massEditFailed: `Mass edit failed`,
    massEditFailedDescription: `Failed to update selected lines. Try again.`,
  },

  // ---------------------------------------------------------------------------
  // Extension type labels
  // ---------------------------------------------------------------------------
  types: {
    line: `Line`,
    ringGroup: `Ring Group`,
    virtualFax: `Virtual Fax Machine`,
    conferenceRoom: `Conference Room`,
    switchboard: `Switchboard`,
    sharedVoicemail: `Shared Voicemail Box`,
    callQueue: `Call Queue`,
  },

  // ---------------------------------------------------------------------------
  // View mode labels
  // ---------------------------------------------------------------------------
  viewModes: {
    extensions: `Extensions`,
    lines: `Lines`,
    ringGroups: `Ring groups`,
    callQueues: `Call queues`,
    conferenceRooms: `Conference rooms`,
    virtualFax: `Virtual fax`,
    sharedVoicemail: `Shared Voicemail`,
  },

  // ---------------------------------------------------------------------------
  // Filters
  // ---------------------------------------------------------------------------
  filters: {
    title: `Filter`,
    status: `Status:`,
    assignedUser: `Assigned user:`,
    assignedProfile: `Assigned profile:`,
    location: `Location:`,
    active: `Active`,
    inactive: `Inactive`,
    all: `All`,
    clearFilters: `Clear filters`,
    apply: `Apply`,
  },

  // ---------------------------------------------------------------------------
  // Placeholder pages
  // ---------------------------------------------------------------------------
  placeholder: {
    notImplemented: `The {{typeLabel}} edit page is not yet implemented.`,
    backToList: `Back to {{listLabel}}`,
  },

  // ---------------------------------------------------------------------------
  // Create extension options
  // ---------------------------------------------------------------------------
  createOptions: {
    add: `Add`,
    addLine: `Add Line`,
    addRingGroup: `Add Ring Group`,
    addCallQueue: `Add Call Queue`,
    addConferenceRoom: `Add Conference Room`,
    addVirtualFax: `Add Virtual Fax Machine`,
    addSharedVoicemail: `Add Shared Voicemail Box`,
  },

  // ---------------------------------------------------------------------------
  // Ordering options
  // ---------------------------------------------------------------------------
  ordering: {
    lastUpdatedNewest: `Last updated (newest first)`,
    lastUpdatedOldest: `Last updated (oldest first)`,
    createdNewest: `Created (newest first)`,
    createdOldest: `Created (oldest first)`,
    nameAZ: `Name (A\u2013Z)`,
    nameZA: `Name (Z\u2013A)`,
    numberAsc: `Number (asc)`,
    numberDesc: `Number (desc)`,
  },

  // ---------------------------------------------------------------------------
  // Slug-to-label mappings (for breadcrumbs)
  // ---------------------------------------------------------------------------
  slugLabels: {
    line: `Line`,
    ringGroup: `Ring Group`,
    callQueue: `Call Queue`,
    conferenceRoom: `Conference Room`,
    virtualFax: `Virtual Fax Machine`,
    sharedVoicemail: `Shared Voicemail Box`,
  },

  // ---------------------------------------------------------------------------
  // List labels for type slugs
  // ---------------------------------------------------------------------------
  listLabels: {
    lines: `Lines`,
    ringGroups: `Ring groups`,
    callQueues: `Call queues`,
    conferenceRooms: `Conference rooms`,
    virtualFax: `Virtual fax`,
    sharedVoicemail: `Shared Voicemail`,
  },
} as const

export type ExtensionsLocale = WidenLocale<typeof extensionsEn>
